import { evaluateConditions } from 'features/libra/conditions/evaluateCondition'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'

import type {
  SignoffConfig,
  SignoffRecord,
  SignoffsField,
  SignoffSlot,
} from './signoffTypes'
import { EMPTY_SLOT_STATE, getActiveSignature } from './signoffTypes'

export interface SignoffRequirement {
  slot: SignoffSlot
  /** False when `requiredWhen` excludes this ticket — waived, not missing. */
  required: boolean
  /** The live signature, when the slot is currently signed. */
  record?: SignoffRecord
  /** The last signature of a slot that was reset; absent if never signed. */
  previous?: SignoffRecord
}

export type SignoffGate =
  | { satisfied: true }
  | { satisfied: false; reason: string }

/**
 * Whether each slot applies to this ticket and where its signatures stand. The
 * panel and the lifecycle gate both read this, so they cannot disagree about
 * which signatures count. `requiredWhen` is evaluated against the ticket's
 * `fieldValues`; signatures come from `signoffs`.
 */
export function resolveSignoffRequirements(
  slots: ReadonlyArray<SignoffSlot>,
  signoffs: SignoffsField,
  fieldValues: FieldValues
): ReadonlyArray<SignoffRequirement> {
  return slots.map((slot) => {
    const state = signoffs[slot.id] ?? EMPTY_SLOT_STATE
    return {
      slot,
      required: evaluateConditions(slot.requiredWhen, fieldValues),
      record: getActiveSignature(state),
      previous: state.signatures.at(-1),
    }
  })
}

/**
 * Whether a transition carrying `requireSignoffs` may run.
 *
 * A missing config blocks rather than passing: `signoffConfigSchema` fails soft,
 * so treating "no slots" as "nothing to sign" would turn a config typo into an
 * unsigned approval.
 */
export function resolveSignoffGate(
  config: SignoffConfig | undefined,
  signoffs: SignoffsField,
  fieldValues: FieldValues
): SignoffGate {
  if (!config) {
    return { satisfied: false, reason: 'Sign-off configuration unavailable' }
  }

  const outstanding = resolveSignoffRequirements(
    config.signoffs,
    signoffs,
    fieldValues
  )
    .filter(({ required, record }) => required && !record)
    .map(({ slot }) => slot.title)

  if (outstanding.length === 0) return { satisfied: true }
  return {
    satisfied: false,
    reason: `Outstanding sign-off: ${outstanding.join(', ')}`,
  }
}
